import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, RefreshCw, Download, Gauge, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { creditReportApi } from '@/services/api';
import { CreditRiskBureauView } from '@/components/loans/CreditRiskDashboard';
import { CreditRiskJustification } from './CreditRiskJustification';
import { SectionHeading } from './CockpitUI';

const reveal = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } };
const inputCls = 'flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition-all';

const clean10 = (v) => String(v || '').replace('+91', '').replace(/\D/g, '').slice(-10);

export const CibilAnalysis = ({ leadId, lead, personal, documents = [], onChanged }) => {
  const [report, setReport] = useState(null);     // parsed_report
  const [score, setScore] = useState(lead?.credit_score || null);
  const [pdfLink, setPdfLink] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const p = personal || {};
  const fromPersonal = useCallback(() => ({
    name: [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || lead?.customer_name || '',
    pan: (p.pan || '').toUpperCase(),
    mobile: clean10(p.mobile || lead?.customer_phone),
    gender: p.gender === 'Female' ? 'Female' : 'Male',
  }), [p.first_name, p.last_name, p.pan, p.mobile, p.gender, lead?.customer_name, lead?.customer_phone]);

  const [form, setForm] = useState(fromPersonal);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Auto-populate from Personal/Income details whenever they load or change.
  // Only fills fields the rep hasn't already typed into (never clobbers edits).
  useEffect(() => {
    const src = fromPersonal();
    setForm((s) => ({
      name: s.name || src.name,
      pan: s.pan || src.pan,
      mobile: s.mobile || src.mobile,
      gender: s.gender || src.gender,
    }));
  }, [fromPersonal]);

  const loadExisting = useCallback(async () => {
    setLoading(true);
    try {
      const res = await creditReportApi.getByLoanLead(leadId);
      if (res.data?.found && res.data.report) {
        const r = res.data.report;
        setReport(r.parsed_report || r.raw_report || null);
        setScore(r.credit_score);
        setPdfLink(r.pdf_link || null);
        setFetchedAt(r.fetched_at || r.created_at || null);
      }
    } catch (e) {
      /* no existing report — show the fetch form */
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const fetchReport = async () => {
    const pan = (form.pan || '').toUpperCase().trim();
    if (pan.length !== 10) { toast.error('Enter a valid 10-character PAN'); return; }
    if (!form.name.trim()) { toast.error('Name is required (must match the PAN card)'); return; }
    const mobile = clean10(form.mobile);
    if (mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }

    setFetching(true);
    try {
      const res = await creditReportApi.fetchCibilReport({
        name: form.name.trim(),
        pan,
        mobile,
        gender: (form.gender || 'male').toLowerCase() === 'female' ? 'female' : 'male',
        consent: 'Y',
        loan_lead_id: leadId,
      });
      if (res.data?.success) {
        setReport(res.data.parsed_report || null);
        setScore(res.data.credit_score);
        setPdfLink(res.data.pdf_link || null);
        setFetchedAt(res.data.fetched_at || new Date().toISOString());
        toast.success('CIBIL report fetched');
        if (onChanged) await onChanged();
      } else {
        toast.error(res.data?.error || 'Failed to fetch CIBIL report');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'Failed to fetch CIBIL report');
    } finally {
      setFetching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="cibil-loading">
        <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="cibil-step">
      <SectionHeading hint="Banker-grade analysis via Surepass — CIBIL (TransUnion): score, DPD, accounts & enquiries.">
        CIBIL (TransUnion) Credit Analysis
      </SectionHeading>

      {/* Fetch panel */}
      <motion.div {...reveal} className="bg-white border border-slate-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="cibil-fetch-form">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-slate-800">
            {report ? 'Re-pull credit report' : 'Pull credit report'}
          </span>
          {fetchedAt && (
            <span className="text-xs text-slate-400 ml-auto">
              Last fetched {new Date(fetchedAt).toLocaleString('en-IN')}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col space-y-1.5">
            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Name (as per PAN)</Label>
            <Input value={form.name} onChange={(e) => upd('name', e.target.value)} className={inputCls} data-testid="cibil-name-input" />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">PAN</Label>
            <Input value={form.pan} onChange={(e) => upd('pan', e.target.value.toUpperCase())} maxLength={10}
              placeholder="ABCDE1234F" className={`${inputCls} uppercase`} data-testid="cibil-pan-input" />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Mobile</Label>
            <Input value={form.mobile} onChange={(e) => upd('mobile', e.target.value)} maxLength={10} className={inputCls} data-testid="cibil-mobile-input" />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Gender</Label>
            <Select value={form.gender} onValueChange={(v) => upd('gender', v)}>
              <SelectTrigger className={inputCls} data-testid="cibil-gender-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Male" data-testid="cibil-gender-opt-male">Male</SelectItem>
                <SelectItem value="Female" data-testid="cibil-gender-opt-female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={fetchReport} disabled={fetching} className="bg-blue-600 hover:bg-blue-700"
            data-testid={report ? 'cibil-refetch-btn' : 'cibil-fetch-btn'}>
            {fetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : report ? <RefreshCw className="w-4 h-4 mr-2" /> : <Gauge className="w-4 h-4 mr-2" />}
            {fetching ? 'Fetching…' : report ? 'Re-fetch CIBIL' : 'Fetch CIBIL Report'}
          </Button>
          {pdfLink && (
            <a href={pdfLink} target="_blank" rel="noreferrer" data-testid="cibil-pdf-link"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
              <Download className="w-4 h-4" /> Download bureau PDF
            </a>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-slate-400 ml-auto">
            <Lock className="w-3 h-3" /> Soft pull · customer consent recorded
          </span>
        </div>
      </motion.div>

      {/* Analysis */}
      {report ? (
        <motion.div {...reveal} data-testid="cibil-analysis">
          <CreditRiskBureauView report={report} creditScore={score} bureauName="CIBIL" bureauColor="blue" />
          <CreditRiskJustification leadId={leadId} report={report} documents={documents} onChanged={onChanged} />
        </motion.div>
      ) : (
        <div className="text-center py-16 text-slate-500 border border-dashed border-slate-200 rounded-xl bg-slate-50/50" data-testid="cibil-empty-state">
          <ShieldCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="font-medium text-slate-600">No CIBIL report yet</p>
          <p className="text-sm">Confirm the PAN, mobile &amp; name above, then fetch the report.</p>
        </div>
      )}
    </div>
  );
};

export default CibilAnalysis;
